import { ApplicationConfig, importProvidersFrom, isDevMode, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter, withInMemoryScrolling, withViewTransitions } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { WebshopEffects } from '@store/effects/webshop.effects';
import { CategoryEffects } from '@store/effects/category.effects';
import { provideStore } from '@ngrx/store';
import { webshopReducer } from '@store/reducers/webshop.reducer';
import { filterReducer } from '@store/reducers/filter.reducer';
import { serviceStatusReducer } from '@store/reducers/service-status.reducer';
import { photoGalleryReducer } from '@store/reducers/photo-gallery.reducer';
import { customerReducer } from '@store/reducers/customer.reducer';
import { companyReducer } from '@store/reducers/company.reducer';
import { categoryReducer } from '@store/reducers/category.reducer';
import { componentsReducer } from '@store/reducers/component.reducer';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { PhotoGalleryEffects } from '@store/effects/photo-gallery.effects';
import { routes } from '@modules/shared/app.routes';
import { CustomerEffects } from '@store/effects/customer.effects';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { CompanyEffects } from '@store/effects/company.effects';
import { attributeReducer } from '@store/reducers/attribute.reducer';
import { AttributeEffects } from '@store/effects/attribute.effects';
import { ProductEffects } from '@store/effects/product.effects';
import { PageEffects } from '@store/effects/page.effects';
import { productReducer } from '@store/reducers/product.reducer';
import { pageReducer } from '@store/reducers/page.reducer';
import {
  AbstractSecurityStorage,
  authInterceptor,
  DefaultLocalStorageService,
  provideAuth,
  withAppInitializerAuthCheck
} from 'angular-auth-oidc-client';
import { authConfig } from './aws.config';


const MyPreset = definePreset(Aura, {
  semantic: {
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '{zinc.50}',
          100: '{zinc.100}',
          200: '{zinc.200}',
          300: '{zinc.300}',
          400: '{zinc.400}',
          500: '{zinc.500}',
          600: '{zinc.600}',
          700: '{zinc.700}',
          800: '{zinc.800}',
          900: '{zinc.900}',
          950: '{zinc.950}'
        }
      },
      dark: {
        surface: {
          0: '#ffffff',
          50: '{slate.50}',
          100: '{slate.100}',
          200: '{slate.200}',
          300: '{slate.300}',
          400: '{slate.400}',
          500: '{slate.500}',
          600: '{slate.600}',
          700: '{slate.700}',
          800: '{slate.800}',
          900: '{slate.900}',
          950: '{slate.950}'
        }
      },
    },
    primary: {
      50: '#e0f4fc',
      100: '#b3e5f7',
      200: '#81d5f2',
      300: '#4fc5ed',
      400: '#29bae9',
      500: '#3fb6e9',
      600: '#00a8e1',
      700: '#0098d7',
      800: '#0088cd',
      900: '#006bbd'
    }
  }
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withInMemoryScrolling({
      anchorScrolling: 'enabled',
      scrollPositionRestoration: 'enabled'
    }), withViewTransitions()),
    provideZonelessChangeDetection(),
    providePrimeNG({theme: {preset: MyPreset, options: {darkModeSelector: 'none'}}}),
    provideHttpClient(withInterceptors([authInterceptor()])),
    provideEffects([WebshopEffects, PhotoGalleryEffects, CustomerEffects, CompanyEffects, CategoryEffects, AttributeEffects, ProductEffects, PageEffects]),
    provideStore({
      webshops: webshopReducer,
      filters: filterReducer,
      serviceStatus: serviceStatusReducer,
      photoGalleries: photoGalleryReducer,
      customers: customerReducer,
      companies: companyReducer,
      categories: categoryReducer,
      components: componentsReducer,
      attributes: attributeReducer,
      products: productReducer,
      pages: pageReducer
    }),
    importProvidersFrom(StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
    })),
    provideAuth(authConfig, withAppInitializerAuthCheck()),
    {
      provide: AbstractSecurityStorage,
      useClass: DefaultLocalStorageService,
    }
  ],
};
